import { useState } from 'react';
import {
  AppBar,
  Box,
  Chip,
  Container,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { isTabRoot } from '../../utils/navigation';
import { PageTransition } from '../common/PageTransition';
import { PushAskBanner } from '../push/PushAskBanner';
import { MobileQuickNav } from './MobileQuickNav';
import { SideNav } from './SideNav';
import { DRAWER_WIDTH, PAGE_GUTTER } from './navConfig';
import { alpha, shellBackdrop, tokens } from '../../theme/tokens';

export function AppShell({ children, title, subtitle, maxWidth = 'lg' }) {
  const { user } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const goBack = useSmartBack('/');
  const [mobileOpen, setMobileOpen] = useState(false);
  const showBack = !isTabRoot(location.pathname);

  const closeMobile = () => setMobileOpen(false);
  const sidebarWidth = {
    xs: DRAWER_WIDTH.xs,
    md: DRAWER_WIDTH.md,
    lg: DRAWER_WIDTH.lg,
  };

  return (
    <Box
        sx={{
          display: 'flex',
          minHeight: '100dvh',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'clip',
          bgcolor: 'transparent',
        }}
    >
      {isDesktop && (
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          width: sidebarWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: sidebarWidth,
            boxSizing: 'border-box',
            height: '100%',
            maxHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRight: 'none',
            color: tokens.onShell,
            colorScheme: 'dark',
            bgcolor: 'transparent',
            backgroundImage: 'none',
            backgroundColor: 'transparent',
          },
        }}
      >
        <SideNav />
      </Drawer>
      )}

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={closeMobile}
        ModalProps={{ keepMounted: false }}
        sx={{
          display: { xs: 'block', md: 'none' },
          [`& .MuiDrawer-paper`]: {
            width: 'min(288px, 86%)',
            boxSizing: 'border-box',
            top: 0,
            bottom: 0,
            height: 'auto',
            maxHeight: 'none',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: 'none',
            color: tokens.onShell,
            colorScheme: 'dark',
            bgcolor: tokens.canvas,
            backgroundImage: `
              radial-gradient(80% 50% at 0% 0%, ${alpha(tokens.brand, 0.08)}, transparent 55%),
              linear-gradient(180deg, ${tokens.canvas}, ${tokens.shell})
            `,
          },
        }}
      >
        <SideNav onNavigate={closeMobile} />
      </Drawer>

      <Box
        sx={{
          flex: '1 1 0%',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          maxWidth: '100%',
          width: '100%',
          overflowX: 'hidden',
          pl: 'var(--safe-left)',
          pr: 'var(--safe-right)',
          boxSizing: 'border-box',
          bgcolor: 'transparent',
        }}
      >
        <AppBar
          position="sticky"
          color="inherit"
          elevation={0}
          square
          sx={{
            top: 0,
            zIndex: (t) => t.zIndex.appBar,
            flexShrink: 0,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden',
            boxSizing: 'border-box',
            border: 'none',
            borderRadius: 0,
            borderBottom: 'none',
            // Same fixed backdrop as body — seamless color, still opaque vs scroll
            ...shellBackdrop,
            boxShadow: 'none',
            color: tokens.onShell,
            pt: 'var(--safe-top)',
            isolation: 'isolate',
          }}
        >
          <Toolbar
            sx={{
              gap: { xs: 1, sm: 1.5 },
              minHeight: { xs: 52, sm: 58, md: 64 },
              px: PAGE_GUTTER,
              py: { xs: 0.75, sm: 1 },
            }}
          >
            {!isDesktop && (
              <IconButton
                edge="start"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                size="medium"
                sx={{
                  color: tokens.onShell,
                  flexShrink: 0,
                  ml: { xs: -0.5, sm: 0 },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            {showBack ? (
              <IconButton
                edge={isDesktop ? 'start' : false}
                onClick={goBack}
                aria-label="Go back"
                size="medium"
                sx={{ color: tokens.onShell, flexShrink: 0 }}
              >
                <ArrowBackIcon />
              </IconButton>
            ) : null}

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {title ? (
                <Typography
                  variant="h6"
                  component="h1"
                  noWrap
                  sx={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 650,
                    fontSize: { xs: '1.05rem', sm: '1.2rem', md: '1.35rem' },
                    lineHeight: 1.25,
                    color: tokens.onShell,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {title}
                </Typography>
              ) : null}
              {subtitle ? (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    mt: 0.25,
                    display: { xs: 'none', sm: 'block' },
                    fontSize: { sm: '0.875rem', md: '0.9375rem' },
                    color: tokens.onShellMuted,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>

            {user ? (
              <Chip
                size="small"
                variant="outlined"
                color="inherit"
                component={RouterLink}
                to="/profile"
                replace
                clickable
                label={user.name}
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  fontWeight: 600,
                  maxWidth: { md: 180, lg: 240 },
                  flexShrink: 0,
                  borderColor: alpha('#FFFFFF', 0.28),
                  bgcolor: 'transparent',
                  color: tokens.onShell,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            ) : null}
          </Toolbar>
          <MobileQuickNav />
        </AppBar>

        <Box
          component="main"
          sx={{
            flex: 1,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Container
            maxWidth={maxWidth}
            disableGutters
            sx={{
              flex: 1,
              width: '100%',
              maxWidth: '100% !important',
              minWidth: 0,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              color: tokens.onShell,
              py: { xs: 2, sm: 2.5, md: 3 },
              px: PAGE_GUTTER,
              pb: {
                xs: 'calc(24px + var(--safe-bottom) + var(--pwa-banner-offset, 0px))',
                sm: 'calc(28px + var(--safe-bottom) + var(--pwa-banner-offset, 0px))',
                md: 'calc(36px + var(--safe-bottom) + var(--pwa-banner-offset, 0px))',
              },
            }}
          >
            <PageTransition>
              <PushAskBanner />
              {children}
            </PageTransition>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
